/**
 * Folha inferior dos filtros de `/revisao` no viewport estreito.
 * Não busca e não lê a store: o pai passa o rascunho e a contagem.
 */
import { KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from "react";
import { Modal } from "antd";
import { X } from "lucide-react";
import {
  DEFAULT_SESSION_LIMIT,
  type QueueOrder,
} from "../../../../core/store/useRevisaoSelecaoStore";
import ModoDeEstudoSecao from "../ModoDeEstudoSecao";
import {
  REVISAO_FILTER_SHEET_TAB_LABEL,
  REVISAO_FILTER_SHEET_TABS,
  rotuloAplicarFolha,
  type RevisaoFilterSheetOpcao,
  type RevisaoFilterSheetProps,
  type RevisaoFilterSheetTab,
} from "./revisaoFilterSheetContrato";
import RevisaoFilterChip from "./RevisaoFilterChip";
import RevisaoFilterChipGroup from "./RevisaoFilterChipGroup";
import RevisaoFilterOverflow from "./RevisaoFilterOverflow";
import RevisaoFilterSearch from "./RevisaoFilterSearch";
import { revisaoMatchesQuery, revisaoOrderBySelection } from "./optionUtils";
import {
  REVISAO_FILTER_FOCUS_RING,
  REVISAO_VISIBLE_OPTIONS,
  revisaoFilterCx,
  revisaoOverflowMode,
} from "./tokens";

/**
 * Mesma guarda de `parContagem` em revisaoHome.ts: o total nunca fica abaixo
 * do que já está escolhido quando a lista ainda não chegou.
 */
function parContagem(selected: number, total: number): { selected: number; total: number } {
  const safeSelected = Math.max(0, selected);
  return { selected: safeSelected, total: Math.max(0, total, safeSelected) };
}

/** Quantos dos três controles do modo fogem do padrão (desligado, limite 20, vencimento). */
function escolhidosDoModo(ahead: boolean, limit: number, queueOrder: QueueOrder): number {
  let escolhidos = 0;
  if (ahead !== false) escolhidos += 1;
  if (limit !== DEFAULT_SESSION_LIMIT) escolhidos += 1;
  if (queueOrder !== "due") escolhidos += 1;
  return escolhidos;
}

function parDaAba(
  aba: RevisaoFilterSheetTab,
  props: RevisaoFilterSheetProps,
): { selected: number; total: number } {
  switch (aba) {
    case "materia":
      return parContagem(props.materiasSelecionadas.length, props.materias.length);
    case "palavras":
      return parContagem(props.palavrasSelecionadas.length, props.palavras.length);
    case "baralhos": {
      const selecionados = props.baralhosFixados ?? props.baralhosSelecionados;
      return parContagem(selecionados.length, props.baralhos.length);
    }
    case "modo":
      return parContagem(escolhidosDoModo(props.ahead, props.limit, props.queueOrder), 3);
  }
}

/**
 * Valores escolhidos ficam na janela visível. `revisaoOrderBySelection` ordena
 * strings, então isto volta para a opção cujo rótulo difere do valor guardado.
 */
function orderOptionsBySelection(
  options: RevisaoFilterSheetOpcao[],
  selected: string[],
): RevisaoFilterSheetOpcao[] {
  const byValue = new Map(options.map((option) => [option.value, option]));
  return revisaoOrderBySelection(
    options.map((option) => option.value),
    selected,
  ).flatMap((value) => {
    const option = byValue.get(value);
    return option ? [option] : [];
  });
}

function NoMatches({ onClear }: { onClear: () => void }) {
  return (
    <div className="rounded-chip border border-dashed border-edge px-4 py-6 text-center">
      <p className="text-[13px] font-medium text-ink">Nenhum filtro encontrado</p>
      <p className="mt-1 text-xs text-ink-muted">Tente outro termo de busca.</p>
      <button
        type="button"
        onClick={onClear}
        className={revisaoFilterCx(
          "mt-2 rounded-chip px-1 text-xs font-medium text-brand hover:underline",
          REVISAO_FILTER_FOCUS_RING,
        )}
      >
        Limpar busca
      </button>
    </div>
  );
}

/**
 * Lista completa de uma dimensão. Vive aqui porque valor e rótulo diferem
 * (id da matéria, "Nome · contagem" do baralho); `RevisaoFilterOptionsDialog`
 * só aceita `string[]` e imprime a string como rótulo. Mesmo desenho de
 * `DimensaoSecao`.
 */
function FolhaOpcoesDialog({
  title,
  noun,
  options,
  selected,
  onToggle,
  onClose,
  searchPlaceholder,
  disabled,
  describedBy,
}: {
  title: string;
  noun: string;
  options: RevisaoFilterSheetOpcao[];
  selected: string[];
  onToggle: (value: string) => void;
  onClose: () => void;
  searchPlaceholder: string;
  /** Mirrors the aba's own disabled state — "Ver todas" is the same options, not a loophole around it. */
  disabled?: boolean;
  describedBy?: string;
}) {
  const [query, setQuery] = useState("");

  const visible = useMemo(() => {
    const matching = options.filter((option) => revisaoMatchesQuery(option.label, query));
    return orderOptionsBySelection(matching, selected);
  }, [options, query, selected]);

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      title={null}
      width="min(760px, 94vw)"
      destroyOnClose
      className="acertei-filter-modal"
      aria-label={title}
    >
      <div className="flex max-h-[80dvh] flex-col bg-surface sm:max-h-[70vh]">
        <div className="border-b border-edge px-4 py-4 sm:px-5">
          <h2 className="text-base font-semibold text-ink">{title}</h2>
          <p className="mt-0.5 text-[13px] text-ink-muted">
            {`${options.length} ${noun} · ${selected.length} selecionadas`}
          </p>
          <RevisaoFilterSearch
            className="mt-3"
            variant="section"
            value={query}
            onChange={setQuery}
            placeholder={searchPlaceholder}
            ariaLabel={`Buscar em ${title}`}
          />
        </div>

        <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-4 py-4 sm:px-5">
          {visible.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-sm font-medium text-ink">Nenhum filtro encontrado</p>
              <p className="mt-1 text-[13px] text-ink-muted">Tente outro termo de busca.</p>
            </div>
          ) : (
            <RevisaoFilterChipGroup ariaLabel={title}>
              {visible.map((option) => (
                <RevisaoFilterChip
                  key={option.value}
                  label={option.label}
                  selected={selected.includes(option.value)}
                  disabled={disabled}
                  describedBy={describedBy}
                  onToggle={() => onToggle(option.value)}
                />
              ))}
            </RevisaoFilterChipGroup>
          )}
        </div>

        <div className="border-t border-edge px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] text-right sm:px-5">
          <button
            type="button"
            onClick={onClose}
            className="h-10 w-full rounded-input bg-brand px-5 text-sm font-semibold text-brand-contrast transition-[filter] hover:brightness-110 sm:w-auto"
          >
            Concluir
          </button>
        </div>
      </div>
    </Modal>
  );
}

function CorpoDimensao({
  title,
  noun,
  options,
  selected,
  onToggle,
  searchPlaceholder,
  emptyLabel,
  query,
  onQuery,
  desabilitada,
  fixadas,
}: {
  title: string;
  noun: string;
  options: RevisaoFilterSheetOpcao[];
  selected: string[];
  onToggle: (value: string) => void;
  searchPlaceholder: string;
  emptyLabel: string;
  query: string;
  onQuery: (value: string) => void;
  desabilitada?: { motivo: string };
  fixadas?: string[];
}) {
  const [expanded, setExpanded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const motivoId = useId();

  const hasOptions = options.length > 0;
  // A fixed day overrides the draft selection for display (design.md Decisão 5)
  // and, on its own, makes the whole aba non-actionable — not just the values
  // it names. Mirrors DimensaoSecao's sectionDisabled/effectiveSelected.
  const sectionDisabled = Boolean(desabilitada) || Boolean(fixadas && fixadas.length > 0);
  const describedBy = desabilitada ? motivoId : undefined;
  const effectiveSelected = fixadas ?? selected;

  const ordered = useMemo(() => {
    const matching = options.filter((option) => revisaoMatchesQuery(option.label, query));
    return orderOptionsBySelection(matching, effectiveSelected);
  }, [options, query, effectiveSelected]);

  const mode = revisaoOverflowMode(ordered.length);
  const windowed =
    mode === "inline" && !expanded ? ordered.slice(0, REVISAO_VISIBLE_OPTIONS) : ordered;
  const shown = mode === "modal" ? windowed.slice(0, REVISAO_VISIBLE_OPTIONS) : windowed;
  const showOverflow = ordered.length > 0 && mode !== "none";

  return (
    <div className="flex flex-col gap-3 px-4 py-4">
      {desabilitada ? (
        <p id={motivoId} className="text-[13px] text-ink-muted">
          {desabilitada.motivo}
        </p>
      ) : null}

      {hasOptions ? (
        <RevisaoFilterSearch
          variant="section"
          value={query}
          onChange={onQuery}
          placeholder={searchPlaceholder}
          ariaLabel={`Buscar em ${title}`}
        />
      ) : null}

      {showOverflow ? (
        <div className="flex justify-end">
          <RevisaoFilterOverflow
            mode={mode}
            total={ordered.length}
            expanded={expanded}
            onToggleExpand={() => setExpanded((current) => !current)}
            onViewAll={() => setDialogOpen(true)}
            noun={noun}
          />
        </div>
      ) : null}

      {!hasOptions ? (
        <p className="text-sm text-ink-muted">{emptyLabel}</p>
      ) : ordered.length === 0 ? (
        <NoMatches onClear={() => onQuery("")} />
      ) : (
        <RevisaoFilterChipGroup ariaLabel={title}>
          {shown.map((option) => (
            <RevisaoFilterChip
              key={option.value}
              label={option.label}
              selected={effectiveSelected.includes(option.value)}
              disabled={sectionDisabled}
              describedBy={describedBy}
              onToggle={() => onToggle(option.value)}
            />
          ))}
        </RevisaoFilterChipGroup>
      )}

      {dialogOpen && hasOptions ? (
        <FolhaOpcoesDialog
          title={title}
          noun={noun}
          options={options}
          selected={effectiveSelected}
          onToggle={onToggle}
          onClose={() => setDialogOpen(false)}
          searchPlaceholder={searchPlaceholder}
          disabled={sectionDisabled}
          describedBy={describedBy}
        />
      ) : null}
    </div>
  );
}

interface DimensaoDaAba {
  title: string;
  noun: string;
  options: RevisaoFilterSheetOpcao[];
  selected: string[];
  onToggle: (value: string) => void;
  searchPlaceholder: string;
  emptyLabel: string;
  desabilitada?: { motivo: string };
  fixadas?: string[];
}

function dimensaoDaAba(props: RevisaoFilterSheetProps): DimensaoDaAba | null {
  switch (props.aba) {
    case "materia":
      return {
        title: REVISAO_FILTER_SHEET_TAB_LABEL.materia,
        noun: "matérias",
        options: props.materias.map((materia) => ({
          value: materia.value,
          label: materia.count > 0 ? `${materia.label} · ${materia.count}` : materia.label,
        })),
        selected: props.materiasSelecionadas,
        onToggle: props.onToggleMateria,
        searchPlaceholder: "Buscar matéria…",
        emptyLabel: "Nenhuma matéria disponível.",
        desabilitada: props.materiaDesabilitada,
      };
    case "palavras":
      return {
        title: REVISAO_FILTER_SHEET_TAB_LABEL.palavras,
        noun: "palavras-chave",
        options: props.palavras.map((palavra) => ({
          value: palavra.name,
          label: palavra.count > 0 ? `${palavra.name} · ${palavra.count}` : palavra.name,
        })),
        selected: props.palavrasSelecionadas,
        onToggle: props.onTogglePalavra,
        searchPlaceholder: "Buscar palavra-chave…",
        emptyLabel: "Nenhuma palavra-chave disponível.",
        desabilitada: props.palavrasDesabilitada,
      };
    case "baralhos":
      return {
        title: REVISAO_FILTER_SHEET_TAB_LABEL.baralhos,
        noun: "baralhos",
        options: props.baralhos.map((baralho) => ({
          value: baralho.name,
          label: baralho.count > 0 ? `${baralho.name} · ${baralho.count}` : baralho.name,
        })),
        selected: props.baralhosSelecionados,
        onToggle: props.onToggleBaralho,
        searchPlaceholder: "Buscar baralho…",
        emptyLabel: "Nenhum baralho disponível.",
        desabilitada: props.baralhosDesabilitada,
        fixadas: props.baralhosFixados,
      };
    case "modo":
      return null;
  }
}

export default function RevisaoFilterSheet(props: RevisaoFilterSheetProps): JSX.Element {
  const { open, onClose, aba, onAba, applying, resultCount, onApply, onClear } = props;

  const diaFixadoAtivo = Boolean(
    props.materiaDesabilitada || props.palavrasDesabilitada || props.baralhosDesabilitada,
  );

  const titleId = useId();
  const panelId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  const [abaDaBusca, setAbaDaBusca] = useState(aba);

  if (abaDaBusca !== aba) {
    setAbaDaBusca(aba);
    setQuery("");
  }

  useEffect(() => {
    if (!open) return;
    panelRef.current?.focus();
  }, [open]);

  if (!open) return <></>;

  const dimensao = dimensaoDaAba(props);
  // Zero cartões ainda se aplica: a página é quem desliga "Iniciar". O botão
  // só espera a gravação. O rótulo continua dizendo a contagem, inclusive 0.

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Escape") return;
    event.preventDefault();
    event.stopPropagation();
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-ink/40" aria-hidden="true" onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className="fixed inset-x-0 bottom-0 z-50 flex max-h-[min(694px,90dvh)] flex-col overflow-hidden rounded-t-modal border border-edge bg-surface text-ink outline-none"
      >
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-edge px-4 py-3">
          <h2 id={titleId} className="m-0 text-base font-semibold text-ink">
            Filtros
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar filtros"
            className={revisaoFilterCx(
              "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-chip",
              "text-ink-secondary transition-colors hover:bg-surface-hover hover:text-ink",
              REVISAO_FILTER_FOCUS_RING,
            )}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div
          role="tablist"
          aria-label="Dimensões do filtro"
          className="grid shrink-0 grid-cols-2 gap-2 border-b border-edge px-4 py-3"
        >
          {REVISAO_FILTER_SHEET_TABS.map((tab) => {
            const selecionada = aba === tab;
            const par = parDaAba(tab, props);
            return (
              <button
                key={tab}
                type="button"
                role="tab"
                id={`${panelId}-${tab}`}
                aria-selected={selecionada}
                aria-controls={panelId}
                onClick={() => onAba(tab)}
                className={revisaoFilterCx(
                  "flex min-w-0 items-center justify-between gap-2 rounded-chip border px-2.5 py-2 text-left",
                  "transition-colors duration-150",
                  REVISAO_FILTER_FOCUS_RING,
                  selecionada
                    ? "border-select/[0.42] bg-select/[0.14] font-semibold text-select-ink"
                    : "border-edge font-medium text-ink-secondary hover:bg-surface-hover hover:text-ink",
                )}
              >
                <span className="min-w-0 text-[13px] leading-snug">
                  {REVISAO_FILTER_SHEET_TAB_LABEL[tab]}
                </span>
                <span
                  className="shrink-0 text-[11px] font-semibold tabular-nums"
                  aria-label={`${par.selected} de ${par.total} selecionados`}
                >
                  {`${par.selected}/${par.total}`}
                </span>
              </button>
            );
          })}
        </div>

        <div
          role="tabpanel"
          id={panelId}
          aria-labelledby={`${panelId}-${aba}`}
          className="min-h-0 flex-1 overflow-y-auto"
        >
          {dimensao ? (
            <CorpoDimensao key={aba} {...dimensao} query={query} onQuery={setQuery} />
          ) : (
            <div className="px-4 py-4">
              <ModoDeEstudoSecao
                ahead={props.ahead}
                onAhead={props.onAhead}
                limit={props.limit}
                onLimit={props.onLimit}
                queueOrder={props.queueOrder}
                onQueueOrder={props.onQueueOrder}
              />
            </div>
          )}
        </div>

        <div className="flex shrink-0 flex-col gap-2 border-t border-edge bg-surface px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          {diaFixadoAtivo ? (
            <p className="text-[12px] text-ink-muted">
              O dia fixado decide os cartões da fila. Solte o dia para aplicar matéria,
              palavra-chave ou baralho.
            </p>
          ) : null}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClear}
              className={revisaoFilterCx(
                "h-10 shrink-0 rounded-input border border-edge bg-surface px-4 text-sm font-medium text-ink-secondary",
                "transition-colors hover:bg-surface-hover hover:text-ink",
                REVISAO_FILTER_FOCUS_RING,
              )}
            >
              Limpar
            </button>
            <button
              type="button"
              onClick={onApply}
              disabled={applying === true}
              className={revisaoFilterCx(
                "inline-flex h-10 min-w-0 flex-1 items-center justify-center rounded-input bg-brand px-4",
                "text-sm font-semibold text-brand-contrast",
                "transition-[filter,opacity] hover:brightness-110",
                "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:brightness-100",
                REVISAO_FILTER_FOCUS_RING,
              )}
            >
              {rotuloAplicarFolha(resultCount)}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
